import React, { useState } from "react";

import { type ActionItemIntegration, type ActionItemIntegrationWithPanes, isActionItemIntegrationWithPanes } from ".";
import { faCheck, faChevronRight } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { showFeatureModal } from "~/billing";
import { billingUpgradeIconDef } from "~/billing/utils";
import type { AsanaPerItemModalProps } from "~/settings/Integrations/Asana/AsanaIntegration";

import { asanaKey, displayAsanaActionItemLinkingModal } from "../asana";
import { jiraKey } from "../jira";
import { IntegrationLink } from "../jira/IntegrationLink";
import displayPromptInstallingJira from "../jira/PromptInstallingJira";
import { type ActionItemLinkingModalProps, displayActionItemLinkingModal } from "./actionItemLinkingModal";
import type { AllActionItemIntegrationsModalProps } from "./allActionItemIntegrationsModal";
import { promptConnectingIntegrationModal } from "./promptConnectingIntegration";

import styles from "./css/kebabModal.module.css";

export type ActionItemIntegrationKebabModalCardProps = AllActionItemIntegrationsModalProps & {
	integration: ActionItemIntegration;
	closeParentModal: () => void;
};

export function ActionItemIntegrationKebabModalCard({
	integration,
	actionItemLinkingModalProps,
	billingFeatureAccess,
	activeIntegrations,
	jiraProps,
	closeParentModal,
}: ActionItemIntegrationKebabModalCardProps) {
	const [isConnected, setIsConnected] = useState<boolean>(activeIntegrations.has(integration.key));

	const displayPaywall = React.useCallback(() => {
		showFeatureModal(integration.billingFeatureName);
	}, [integration.billingFeatureName]);

	const hasAccessToIntegration = billingFeatureAccess?.[integration.billingFeatureName] || false;

	const connectIntegrationOrAuth = React.useCallback(() => {
		if (integration.key === jiraKey) {
			if (!isConnected) {
				displayPromptInstallingJira();
			} else {
				new IntegrationLink(jiraProps);
				closeParentModal();
			}
		}

		if (integration.key === asanaKey) {
			const asanaProps: AsanaPerItemModalProps = {
				userId: actionItemLinkingModalProps?.userId,
				blockKey: actionItemLinkingModalProps.blockKey,
				noteId: actionItemLinkingModalProps.noteId,
				isAsanaConnected: isConnected,
			};

			const displayAsanaModal = () =>
				displayAsanaActionItemLinkingModal(asanaProps).then(result => {
					if (result === true) closeParentModal();
				});

			if (!isConnected) {
				promptConnectingIntegrationModal(integration).then(connectSuccess => {
					if (connectSuccess) {
						setIsConnected(true);
						displayAsanaModal();
					}
				});
			} else {
				displayAsanaModal();
			}
		} else {
			const linkingModalProps: ActionItemLinkingModalProps = {
				integration: integration as ActionItemIntegrationWithPanes,
				...actionItemLinkingModalProps,
			};

			const displayIntegrationModal = () => {
				displayActionItemLinkingModal(linkingModalProps).then(result => {
					if (result === true) closeParentModal();
				});
			};

			if (!isConnected) {
				promptConnectingIntegrationModal(integration).then(connectSuccess => {
					if (connectSuccess) {
						setIsConnected(true);
						displayIntegrationModal();
					}
				});
			} else if (isActionItemIntegrationWithPanes(integration)) {
				displayIntegrationModal();
			}
		}
	}, [integration, actionItemLinkingModalProps, isConnected, jiraProps, closeParentModal]);

	return (
		<button
			className={styles.integrationCard}
			onClick={hasAccessToIntegration ? connectIntegrationOrAuth : displayPaywall}
		>
			<div className={styles.cardContentSection}>
				<div className={styles.logo}>{integration.logo}</div>
				{integration.displayName}
			</div>
			<div className={styles.cardContentSection}>
				{!hasAccessToIntegration && <FontAwesomeIcon icon={billingUpgradeIconDef} color={"var(--billingPro)"} />}
				{isConnected ? (
					<div className={styles.connectedBadge}>
						<FontAwesomeIcon icon={faCheck} />
						Connected
					</div>
				) : (
					<div className={styles.notConnectedBadge}>Not connected</div>
				)}
				<FontAwesomeIcon icon={faChevronRight} color="#6A7980" />
			</div>
		</button>
	);
}
